import { IconType } from '@assembly-js/design-system';
import { useCallback } from 'react';

import { useAppDispatch } from 'src/hooks/useStore';
import { ApiTags } from 'src/services/api';
import { useUpdateAppBuilderMutation } from 'src/services/api/appBuilderApi';
import {
  applicationSettingsApi,
  useGetAppQuery,
  useGetInstallsQuery,
  useUpdateAppMutation,
} from 'src/services/api/applicationsApi';
import {
  updateExtensionSettingsAction,
  updateModuleSettings,
} from 'src/store/settings/actions';

export const useUpdateAppSettings = (moduleId: string) => {
  const dispatch = useAppDispatch();
  const { data: { moduleSettings, extensionsSettings } = {}, isLoading } =
    useGetInstallsQuery();

  const module = moduleSettings?.find((m) => m.id === moduleId);
  const extension = extensionsSettings?.[moduleId];
  const appId = extension?.appId;
  const appBuilderId = extension?.appBuilderId;

  const { data: existingApp, isLoading: isAppLoading } = useGetAppQuery(
    { id: appId! },
    { skip: !appId },
  );
  const [updateAppMutation, { isLoading: isMutating }] = useUpdateAppMutation();
  const [updateAppBuilder] = useUpdateAppBuilderMutation();

  const currentTitle = module?.label ?? '';
  const currentIcon = module?.icon;

  const updateSettings = useCallback(
    async ({ name, icon }: { name: string; icon: string }) => {
      if (!module) throw new Error('Module not found');

      // App-builder ("Vibe App") installs keep name/icon on the AppBuilder
      // record, which the publish popover reads and the deploy job uses. Route
      // the edit there (not the module-only path) so both stay in sync — the
      // backend PATCH mirrors the change down into module_settings. Otherwise
      // the popover and the published app keep the stale values.
      if (appBuilderId) {
        const patchResult = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            undefined,
            (draft) => {
              const idx = draft.moduleSettings.findIndex(
                (m) => m.id === moduleId,
              );
              if (idx !== -1) {
                draft.moduleSettings[idx] = {
                  ...draft.moduleSettings[idx],
                  label: name,
                  icon,
                };
              }
              if (draft.extensionsSettings?.[moduleId]) {
                draft.extensionsSettings[moduleId] = {
                  ...draft.extensionsSettings[moduleId],
                  name,
                  icon: icon as IconType,
                };
              }
            },
          ),
        );

        try {
          await updateAppBuilder({ id: appBuilderId, name, icon }).unwrap();
        } catch (e) {
          patchResult.undo();
          throw e;
        }
        return;
      }

      if (module.type === 'module') {
        const patchResult = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            undefined,
            (draft) => {
              const idx = draft.moduleSettings.findIndex(
                (m) => m.id === moduleId,
              );
              if (idx !== -1) {
                draft.moduleSettings[idx] = {
                  ...draft.moduleSettings[idx],
                  label: name,
                  icon,
                };
              }
            },
          ),
        );

        try {
          await dispatch(
            updateModuleSettings(
              (moduleSettings ?? []).map((m) =>
                m.id === moduleId ? { ...m, label: name, icon } : m,
              ),
              { disableSuccessAlert: true },
            ),
          );
        } catch (e) {
          patchResult.undo();
          throw e;
        }
      } else if (appId && existingApp) {
        const patchResult = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            undefined,
            (draft) => {
              const idx = draft.moduleSettings.findIndex(
                (m) => m.id === moduleId,
              );
              if (idx !== -1) {
                draft.moduleSettings[idx] = {
                  ...draft.moduleSettings[idx],
                  label: name,
                  icon,
                };
              }
            },
          ),
        );

        try {
          await updateAppMutation({
            ...existingApp,
            name,
            icon: icon as IconType,
            moduleId,
          });
          dispatch(applicationSettingsApi.util.invalidateTags([ApiTags.apps]));
        } catch (e) {
          patchResult.undo();
          throw e;
        }
      } else {
        const patchResult = dispatch(
          applicationSettingsApi.util.updateQueryData(
            'getInstalls',
            undefined,
            (draft) => {
              const idx = draft.moduleSettings.findIndex(
                (m) => m.id === moduleId,
              );
              if (idx !== -1) {
                draft.moduleSettings[idx] = {
                  ...draft.moduleSettings[idx],
                  label: name,
                  icon,
                };
              }
              if (draft.extensionsSettings?.[moduleId]) {
                draft.extensionsSettings[moduleId] = {
                  ...draft.extensionsSettings[moduleId],
                  name,
                  icon: icon as IconType,
                };
              }
            },
          ),
        );

        try {
          if (extensionsSettings) {
            await dispatch(
              updateExtensionSettingsAction({
                ...extensionsSettings,
                [moduleId]: {
                  ...extensionsSettings[moduleId],
                  name,
                  icon: icon as IconType,
                },
              }),
            );
          }

          await dispatch(
            updateModuleSettings(
              (moduleSettings ?? []).map((m) =>
                m.id === moduleId ? { ...m, label: name, icon } : m,
              ),
              { disableSuccessAlert: true },
            ),
          );
        } catch (e) {
          patchResult.undo();
          throw e;
        }
      }
    },
    [
      module,
      moduleSettings,
      extensionsSettings,
      appId,
      appBuilderId,
      existingApp,
      moduleId,
      dispatch,
      updateAppMutation,
      updateAppBuilder,
    ],
  );

  return {
    updateSettings,
    isLoading: isLoading || isMutating || isAppLoading,
    currentTitle,
    currentIcon,
  };
};
